import {
  Component,
  signal,
  inject,
  effect,
  model,
  computed,
  output,
  viewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { InputBuscador } from '../input-buscador/input-buscador';
import { IProductoData } from '@core/interfaces/entidades/producto.entidad';
import { ProductoApiService } from '@core/services/api/producto.service';
import { AppService } from '@shared/services/app.service';
import { SucursalService } from '@shared/services/sucursal.service';
import { CategoriaApiService } from '@core/services/api/categoria-api.service';
import { ICategoriaData } from '@core/interfaces/entidades/categoria.entidad';
import { ToastService } from '@shared/services/toast.service';
import { IPaginar } from '@core/interfaces/paginacion/paginacion.interface';
import { Paginacion } from '../paginacion/paginacion';
import { FormsModule } from '@angular/forms';
import { UiDialog, UiInputNumber, UiTextarea, UiTooltip, UiButton } from '@shared/components/ui';
import { AgregarProductoLibre } from '../agregar-producto-libre/agregar-producto-libre';
import { IProductoLibre } from '@core/interfaces/producto.libre.interface';
import { IProductoCarritoData } from '@core/interfaces/entidades/productoCarrito.entidad';
import { buildProductoToCarritoCompra } from '@shared/utils/functions.utils';

@Component({
  selector: 'app-agregar-producto-venta',
  imports: [
    CommonModule,
    InputBuscador,
    Paginacion,
    FormsModule,
    UiTextarea,
    UiInputNumber,
    UiButton,
    UiTooltip,
    UiDialog,
    AgregarProductoLibre,
  ],
  templateUrl: './agregar-producto-venta.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './agregar-producto-venta.css',
})
export class AgregarProductoVenta {
  constructor() {
    effect(() => {
      if (this.appService.authReady() && this.sucursalService.sucursalReady()) {
        this.categoriaApiService.cargar().subscribe({
          next: (categorias) => this.categorias.set(categorias),
          error: (err) => {
            if (err.status === 401) return;
            this.toastService.show(
              'top-right',
              'error',
              'Error',
              err.error.message || 'Error al cargar categorías',
            );
            console.error('Error al cargar categorías:', err);
          },
        });
        this.cambiarPagina(1);
      }
    });
  }
  // servicios
  private appService = inject(AppService);
  private productoApiService = inject(ProductoApiService);
  private categoriaApiService = inject(CategoriaApiService);
  private sucursalService = inject(SucursalService);
  private toastService = inject(ToastService);
  // data
  private itemsPorPagina = 18;
  busqueda = model<string>('');
  productos_seleccionados = signal<IProductoCarritoData[]>([]);
  paginacion = signal<IPaginar<IProductoData> | null>(null);
  categorias = signal<ICategoriaData[]>([]);
  categoria_seleccionada = model<number | null>(null);
  modal_producto_libre = signal(false);
  cerrar = output<void>();
  inputBuscador = viewChild<InputBuscador>('inputBuscador');

  totalProductosSeleccionados = computed(() => {
    return this.productos_seleccionados().reduce((total, producto) => total + producto.cantidad, 0);
  });
  totalPrecioProductosSeleccionados = computed(() => {
    return this.productos_seleccionados().reduce(
      (total, producto) => total + producto.cantidad * producto.precio,
      0,
    );
  });

  // metodos
  onBuscar() {
    const termino = this.busqueda().trim();
    if (termino === '') {
      this.cambiarPagina(1);
      return;
    }
    this.productoApiService.buscarProducto(termino).subscribe((response) => {
      // Si el término coincide exactamente con el código de un producto (pistoleo),
      // se agrega directamente y se deja el buscador listo para el siguiente escaneo.
      if (
        response.data.length === 1 &&
        response.data[0].codigo.toLowerCase() === termino.toLowerCase()
      ) {
        this.agregarProducto(response.data[0]);
        this.inputBuscador()?.reset();
        this.inputBuscador()?.focus();
        return;
      }
      this.paginacion.set({
        data: response.data,
        total: response.data.length,
        size: response.data.length,
        pagina: 1,
      });
    });
  }

  cambiarPagina(nuevaPagina: number) {
    this.productoApiService
      .paginar(nuevaPagina, this.itemsPorPagina, this.categoria_seleccionada() ?? undefined)
      .subscribe({
        next: (response) => {
          const soloProductosConStock = response.data.filter(
            (producto) =>
              (producto.stock_sucursal.controlar_stock === true &&
                producto.stock_sucursal &&
                producto.stock_sucursal.stock > 0) ||
              producto.stock_sucursal.controlar_stock === false,
          );
          this.paginacion.set({
            ...response,
            data: soloProductosConStock,
          });
        },
        error: (err) => {
          if (err.status == 401) return;
          this.toastService.show(
            'top-right',
            'error',
            'Error',
            err.error.message ?? 'No se pudo cargar la lista de productos.',
          );
        },
      });
  }
  agregarProducto(producto: IProductoData): void {
    if (producto.stock_sucursal.controlar_stock === true && producto.stock_sucursal.stock === 0) {
      this.toastService.show(
        'top-right',
        'warn',
        'Sin stock',
        `El producto "${producto.nombre}" no tiene stock disponible en esta sucursal.`,
      );
      this.inputBuscador()?.focus();
      return;
    }
    const yaSeleccionado = this.productos_seleccionados().some((p) => p.id === producto.id);
    if (!yaSeleccionado) {
      const nuevoProducto: IProductoCarritoData = {
        ...buildProductoToCarritoCompra(producto),
        cantidad: 1,
      };
      this.productos_seleccionados.update((productos) => [...productos, nuevoProducto]);
    }
    // aumentar la cantidad si ya está seleccionado
    else {
      this.productos_seleccionados.update((productos) =>
        productos.map((p) => {
          if (p.id === producto.id) {
            return { ...p, cantidad: p.cantidad + 1 };
          }
          return p;
        }),
      );
    }
    // Mantiene el foco en el buscador para poder seguir agregando con el pistoleo.
    this.inputBuscador()?.focus();
  }
  onAgregarProducto(producto: IProductoLibre) {
    const nuevoProducto: IProductoCarritoData = {
      id: null,
      codigo: null,
      nombre: producto.nombre,
      stock: Infinity,
      cantidad: producto.cantidad,
      precio: producto.precio,
    };
    this.productos_seleccionados.update((productos) => [...productos, nuevoProducto]);
    this.modal_producto_libre.set(false);
  }
  btnCantidad(producto: IProductoCarritoData, accion: 'aumentar' | 'disminuir'): void {
    this.productos_seleccionados.update((productos) =>
      productos.map((p) => {
        if (p.id === producto.id) {
          if (accion === 'aumentar') {
            return { ...p, cantidad: p.cantidad + 1 };
          } else if (accion === 'disminuir' && p.cantidad > 1) {
            return { ...p, cantidad: p.cantidad - 1 };
          }
        }
        return p;
      }),
    );
  }
  quitarProductoSeleccionado(producto: IProductoCarritoData): void {
    this.productos_seleccionados.update((productos) =>
      productos.filter((p) => p.id !== producto.id),
    );
  }
  filtrarPorCategoria(categoriaId: number | null): void {
    this.categoria_seleccionada.set(categoriaId);
    this.cambiarPagina(1);
  }

  agregar_productos = output<IProductoCarritoData[]>();
  confirmarAgregarProductos() {
    if (this.productos_seleccionados().length === 0) {
      this.toastService.show(
        'top-right',
        'warn',
        'Atención',
        'No has seleccionado ningún producto para agregar.',
      );
      return;
    }
    this.agregar_productos.emit(this.productos_seleccionados());
  }
  cancelarAgregarProductos() {
    this.productos_seleccionados.set([]);
    this.cerrar.emit();
  }
}
